/**
 * Sanitizer for product writes (Firestore batch payloads + audit-log dual-write bodies).
 *
 * Extracted from products.ts and hardened for ATL-554: in Firestore-read mode (launch
 * posture, ATL-462) products in productsDictAtom carry a LIVE Firestore DocumentReference
 * (`product.ref = doc.ref`, AppStack.js) — a cyclic object. The unguarded recursion blew
 * the stack (RangeError, Sentry QARASAY-KASSA-26F/26G/26Y/273) on every product update
 * (composite-ingredient branch) and delete (audit-log dataBefore).
 *
 * Sanitizes by:
 * - Converting mock Timestamps (objects with toDate function) to actual Date objects
 * - Preserving Date instances (Object.entries(date) is [] — they'd corrupt to {})
 * - Dropping `ref` keys and DocumentReference-shaped values (cyclic; the backend resolves
 *   ids itself, and persisted product docs never read `ref` back — it is re-attached from
 *   doc.ref on every subscription read in AppStack)
 * - Removing any function values (Firestore can't accept functions)
 * - Recursively processing nested objects and arrays, cycle-safe (back-edges dropped)
 *
 * Deliberate near-copy of `packages/api-client/src/sanitize.ts` (same isDocumentReference
 * duck-check and WeakSet delete-after-visit DAG guard): that one targets HTTP bodies
 * (serializes Dates to ISO, passes opaque body types), this one targets Firestore writes
 * (must keep real Date objects). Keep the guard logic in sync if either changes.
 *
 * NOTE this deliberately DIVERGES from firebase.ts `sanitizeForFirestoreDeep`, which
 * returns DocumentReferences as-is (no drop), converts toDate objects only when they carry
 * seconds/nanoseconds, and has NO cycle guard — do not "sync" the two blindly.
 */

/** A value shaped like a Firestore DocumentReference (live native or JS SDK, or mock). */
const isDocumentReference = (value: object): boolean => {
  const ctorName = (value as { constructor?: { name?: string } }).constructor?.name || ''
  if (ctorName.includes('Reference')) return true
  return 'firestore' in value && 'path' in value && typeof (value as { path: unknown }).path === 'string'
}

export const sanitizeForFirestore = (value: unknown, seen: WeakSet<object> = new WeakSet()): unknown => {
  // Defensive: `seen` is internal recursion state — no caller should pass it. A misuse
  // that passes a non-WeakSet (e.g. JSON.stringify's `, null, 2` args getting absorbed
  // into this call by a bad wrap) must NOT crash a render. Normalize instead of throwing
  // on `seen.has` (ATL-629: that exact slip white-screened SaleDetail print on web).
  if (!(seen instanceof WeakSet)) seen = new WeakSet()

  if (value === null || value === undefined) return value

  // Skip functions entirely - Firestore can't store them
  if (typeof value === 'function') {
    return undefined
  }

  if (typeof value !== 'object') return value

  // Preserve real Dates — the plain-object branch would turn them into {}
  if (value instanceof Date) return value

  // Drop live/mock DocumentReferences BEFORE recursing — live refs are cyclic (ATL-554)
  if (isDocumentReference(value)) return undefined

  // Check if it's a mock Timestamp (has toDate function) - convert to Date
  if ('toDate' in value && typeof (value as { toDate: unknown }).toDate === 'function') {
    try {
      return (value as { toDate: () => Date }).toDate()
    } catch {
      // Malformed timestamp-like object — null is the safe Firestore fallback (sales.ts convention)
      return null
    }
  }

  // Cycle guard: a value already on the current path is a back-edge — drop it.
  // Warn: with ref keys skipped and DocumentReferences dropped above, a surviving cycle is
  // anomalous — make the (silent) data drop observable instead of corrupting quietly.
  if (seen.has(value)) {
    console.warn('[productSanitize] dropped cyclic value during sanitization')
    return undefined
  }
  seen.add(value)

  let result: unknown
  if (Array.isArray(value)) {
    result = value.map(item => sanitizeForFirestore(item, seen)).filter(item => item !== undefined)
  } else {
    const sanitizedObj: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(value)) {
      // Never carry ref fields into Firestore payloads / HTTP bodies
      if (key === 'ref') continue
      // Skip undefined values and functions
      if (val !== undefined && typeof val !== 'function') {
        const sanitized = sanitizeForFirestore(val, seen)
        if (sanitized !== undefined) {
          sanitizedObj[key] = sanitized
        }
      }
    }
    result = sanitizedObj
  }

  // Allow the same object to reappear on a sibling branch (DAG, not a cycle).
  seen.delete(value)
  return result
}
